import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { permissionService } from "../../services/api";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  OUTWARD_BUSINESS_TZ,
  mapPermissionRequestError,
  normalizeYmdIST,
  todayYmdIST,
  validatePermissionRequestRange,
  PERMISSION_TYPE_OUTWARD_BACKDATE,
} from "../../utils/outwardBackdatePermission";

dayjs.extend(utc);
dayjs.extend(timezone);

function formatDisplayDate(ymd) {
  if (!ymd) return "—";
  const d = dayjs.tz(ymd, OUTWARD_BUSINESS_TZ);
  return d.isValid() ? d.format("DD-MM-YYYY") : ymd;
}

export default function OutwardRequestAccessModal({
  open,
  onClose,
  onCancel,
  onSuccess,
  warehouseId,
  selectedOldDate = "",
  isPending = false,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const todayYmd = todayYmdIST();

  useEffect(() => {
    if (!open) return;
    setFormError("");
    setSubmitting(false);
    const prefilled = normalizeYmdIST(selectedOldDate) || todayYmd;
    setFromDate(prefilled);
    setToDate(prefilled);
  }, [open, selectedOldDate, warehouseId, isPending, todayYmd]);

  useEffect(() => {
    if (!open) return undefined;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) {
        if (isPending) onClose?.();
        else onCancel?.();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
    };
  }, [open, submitting, isPending, onClose, onCancel]);

  if (!open) return null;

  const handleRequestAccess = async () => {
    if (submitting || isPending) return;

    const rangeCheck = validatePermissionRequestRange(fromDate, toDate);
    if (!rangeCheck.ok) {
      setFormError(rangeCheck.error);
      return;
    }

    const requestFromYmd = rangeCheck.fromYmd;
    const requestToYmd = rangeCheck.toYmd;

    setSubmitting(true);
    setFormError("");
    try {
      const res = await permissionService.createRequest({
        permissionType: PERMISSION_TYPE_OUTWARD_BACKDATE,
        warehouseId,
        fromDate: requestFromYmd,
        toDate: requestToYmd,
        reason: `Outward backdate access requested for posting (${requestFromYmd} to ${requestToYmd})`,
      });
      if (!res?.success) {
        throw new Error(res?.error || "Failed to submit request");
      }
      onSuccess?.(res);
    } catch (err) {
      const msg = mapPermissionRequestError(err?.message || err?.error);
      const lower = String(msg).toLowerCase();
      if (lower.includes("pending")) {
        onClose?.({ pendingDetected: true });
        return;
      }
      setFormError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackdrop = () => {
    if (submitting) return;
    if (isPending) onClose?.();
    else onCancel?.();
  };

  return createPortal(
    <div
      className="form-popup-modal fixed inset-0 flex items-center justify-center p-3 md:p-4"
      style={{ zIndex: 100000 }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="outward-permission-request-title"
    >
      <div
        className="form-popup-overlay absolute inset-0"
        onClick={handleBackdrop}
        aria-hidden="true"
      />
      <div
        className="form-popup-container relative mx-auto w-full max-w-md flex flex-col bg-white shadow-xl rounded-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-[#EEF1F5] px-5 py-4">
          <h2 id="outward-permission-request-title" className="form-popup-title m-0">
            Request Access for Old Outward Posting
          </h2>
        </div>
        <div className="px-5 py-4 space-y-4 flex-1">
          {isPending ? (
            <p className="text-sm text-gray-700 leading-relaxed">
              Your outward backdate access request is already pending admin approval.
            </p>
          ) : (
            <>
              <p className="text-sm text-gray-700 leading-relaxed">
                You need admin approval to create outward transactions for older posting dates.
                Select the date range you need access for.
              </p>
              <div className="space-y-3">
                <label className="block text-sm text-gray-800">
                  <span className="font-medium">From Date</span>
                  <input
                    type="date"
                    value={fromDate}
                    max={todayYmd}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-green-600 focus:outline-none focus:ring-1 focus:ring-green-600"
                    disabled={submitting}
                  />
                </label>
                <label className="block text-sm text-gray-800">
                  <span className="font-medium">To Date</span>
                  <input
                    type="date"
                    value={toDate}
                    max={todayYmd}
                    onChange={(e) => setToDate(e.target.value)}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-green-600 focus:outline-none focus:ring-1 focus:ring-green-600"
                    disabled={submitting}
                  />
                </label>
              </div>
              {fromDate && toDate ? (
                <p className="text-sm text-gray-600">
                  Requesting access for{" "}
                  <strong>
                    {formatDisplayDate(fromDate)} → {formatDisplayDate(toDate)}
                  </strong>
                </p>
              ) : null}
              <p className="text-sm text-gray-600">
                If approved, access remains active for the requested date range until an
                administrator revokes it.
              </p>
              {formError ? (
                <p className="text-sm text-red-600 rounded-md bg-red-50 px-3 py-2">
                  {formError}
                </p>
              ) : null}
            </>
          )}
        </div>
        <div className="form-popup-footer border-t border-[#EEF1F5] bg-white px-5 py-3 flex items-center justify-end gap-3 flex-shrink-0">
          {isPending ? (
            <button type="button" onClick={onClose} className="form-popup-btn-outline">
              Close
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={onCancel}
                disabled={submitting}
                className="form-popup-btn-outline"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRequestAccess}
                disabled={submitting}
                className="form-popup-btn-solid"
              >
                {submitting ? "Requesting..." : "Request Access"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
